import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Activity, Eye, KeyRound, UserX, UserCheck, RefreshCw, Shield } from "lucide-react";

const ACTION_META = {
  view_as: { label: "Viewed as", icon: Eye, bg: "rgba(217,119,6,0.12)", color: "#B45309" },
  reset_password: { label: "Reset password", icon: KeyRound, bg: "rgba(64,96,255,0.12)", color: "#4060FF" },
  deactivate: { label: "Deactivated", icon: UserX, bg: "rgba(220,38,38,0.12)", color: "#B91C1C" },
  activate: { label: "Activated", icon: UserCheck, bg: "rgba(0,134,58,0.12)", color: "#00863A" },
};

const FILTERS = [
  { key: "all", label: "All activity" },
  { key: "view_as", label: "View-as" },
  { key: "reset_password", label: "Password resets" },
  { key: "status", label: "Status changes" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function duration(start, end) {
  if (!start) return null;
  if (!end) return { active: true };
  const ms = new Date(end) - new Date(start);
  if (Number.isNaN(ms) || ms < 0) return null;
  if (ms < 60000) return { text: `${Math.round(ms / 1000)}s` };
  if (ms < 3600000) return { text: `${Math.round(ms / 60000)}m` };
  return { text: `${(ms / 3600000).toFixed(1)}h` };
}

export default function AdminActivity() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/activity", { params: { limit: 150 } });
      setRows(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not load activity.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "status") return r.action === "activate" || r.action === "deactivate";
    return r.action === filter;
  });

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const isSuper = !!user?.is_super_admin;

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-10">
        <div className="mb-6 fade-up">
          <div className="pill mb-3"><span className="dot" />Users · Activity</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <Activity size={26} className="text-indigo-600" /> Admin activity
          </h1>
          <p className="text-ink-2 text-sm mt-2 max-w-2xl">
            Every sensitive admin action — <b>view-as</b> sessions, <b>password resets</b> and <b>account status</b> changes.
            {isSuper
              ? " As super admin you see every admin's actions."
              : " You see only the actions you performed yourself."}
          </p>
        </div>

        <div className="glass p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="activity-toolbar">
          <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: "rgba(15,18,38,0.05)" }}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`activity-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.key ? "bg-white text-ink shadow-sm" : "text-ink-2 hover:bg-white/50"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading} className="btn-secondary !py-2 !px-3 text-xs ml-auto" data-testid="activity-refresh">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 chip chip-amber inline-flex items-center gap-2" data-testid="activity-error">{error}</div>
        )}

        <div className="glass overflow-hidden" data-testid="activity-card">
          <div className="overflow-x-auto">
            <table className="modern w-full" data-testid="activity-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target member</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-10 text-ink-3" data-testid="activity-loading">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-10 text-ink-3" data-testid="activity-empty">No activity recorded yet.</td></tr>
                ) : filtered.map((r) => {
                  const meta = ACTION_META[r.action] || { label: r.action, icon: Shield, bg: "rgba(15,18,38,0.08)", color: "#5A607F" };
                  const Icon = meta.icon;
                  const dur = r.action === "view_as" ? duration(r.at, r.ended_at) : null;
                  return (
                    <tr key={r.id} data-testid={`activity-row-${r.id}`}>
                      <td className="text-xs text-ink-3 font-mono whitespace-nowrap">{fmtTime(r.at)}</td>
                      <td className="text-xs font-mono">{r.actor_email || "—"}</td>
                      <td>
                        <span className="chip inline-flex items-center gap-1 text-[11px] font-semibold" style={{ background: meta.bg, color: meta.color }} data-testid={`activity-action-${r.id}`}>
                          <Icon size={12} /> {meta.label}
                        </span>
                      </td>
                      <td className="text-xs">
                        <div className="font-mono">{r.target_email || "—"}</div>
                        <div className="text-ink-3">{r.target_name || "—"}{r.target_role ? ` · ${r.target_role}` : ""}</div>
                      </td>
                      <td className="text-xs text-ink-3">
                        {r.action === "view_as" ? (
                          dur?.active
                            ? <span className="chip chip-amber text-[10px]">Active session</span>
                            : <span className="font-mono">{dur?.text ? `${dur.text} session` : "—"}{r.ip ? ` · ${r.ip}` : ""}</span>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
